import { Fragment, useCallback, useEffect, useMemo, useRef } from "react";

import { MAXIMUM_CLUSTER_DISTANCE_SECONDS } from "@/constants";
import { useEpisode } from "@/containers/Episode/context";
import { useLocalAudio } from "@/containers/Episode/LocalAudioContext";
import { cx, durationFromSeconds, timedWordsToSentence } from "@/utils";
import mergeTranscriptAndTrimmedContent from "@/utils/mergeTranscriptAndTrimmedContent";
import Typography from "@components/Typography";

import styles from "./Karaoke.module.css";

type KaraokeProps = {
  className?: string;
  manualCurrentTime?: number;
};

// Determines how long we prevent auto scroll after a user has interacted by scrolling in the transcript
const USER_INTERACTION_SCROLL_SUSPENSION_TIMEOUT = 3000;

const Karaoke: React.FunctionComponent<KaraokeProps> = ({
  className,
  manualCurrentTime,
  ...rest
}) => {
  const karaokeTimeout = useRef<number | undefined>(undefined);
  const { audio, playing, currentTime } = useLocalAudio({
    currentTime: true,
    playing: true,
  });
  const effectiveTime: number = audio
    ? (currentTime ?? 0)
    : (manualCurrentTime ?? currentTime ?? 0);
  const peep = useRef<HTMLDivElement>(null);
  const active = useRef<HTMLSpanElement>(null);
  const sentencesRef = useRef<HTMLDivElement>(null);
  const scrollIsSuspended = useRef(false);
  const { grouped_times_and_words, audioMentions, removedMentionIds } =
    useEpisode();

  // Prevents the autoscroll from overriding user interactions
  // resets automatically to autoscroll if user does not interact for USER_INTERACTION_SCROLL_SUSPENSION_TIMEOUT.

  const suspendScroll = useCallback(() => {
    // Suspend the scroll
    scrollIsSuspended.current = true;

    // Kill any current timeouts set if the function is called again
    karaokeTimeout.current && window.clearTimeout(karaokeTimeout.current);

    // Create a timeout to reset the behaviour after a given timeframe
    karaokeTimeout.current = window.setTimeout(() => {
      scrollIsSuspended.current = false;
    }, USER_INTERACTION_SCROLL_SUSPENSION_TIMEOUT);
  }, []);

  // If it's overkill to use a memo here? probably. But if the transcripts get huge
  // it will save a lot of computing power on each render.

  const sentences = useMemo(() => {
    if (!grouped_times_and_words)
      return audioMentions.map((m) => ({
        sentence: m.trimmed_content,
        timings: [],
        isRemoved: false,
        isMention: true,
      }));

    const transcript = mergeTranscriptAndTrimmedContent({
      grouped_times_and_words,
      mentions: audioMentions,
      removedMentionIds,
    });

    return timedWordsToSentence(transcript);
  }, [grouped_times_and_words, audioMentions, removedMentionIds]);

  // Scroll to the first mention when mounting.
  useEffect(() => {
    if (peep.current) {
      const firstQuote = peep.current.querySelectorAll("i")[0];
      firstQuote && peep.current.scrollTo({ top: firstQuote.offsetTop });
    }
  }, []);

  // Automatically scroll to the active sentence
  useEffect(() => {
    if (
      scrollIsSuspended.current === false &&
      active.current &&
      sentencesRef.current &&
      peep.current
    ) {
      const word = active.current;
      const scrollDistance = peep.current.scrollHeight;
      const wordRect = word.getBoundingClientRect();
      const visibleHeight = peep.current.clientHeight;

      const wordTop = word.offsetTop;
      const wordBottom = wordRect.height + wordTop;

      if (
        (wordBottom >= visibleHeight || wordTop < scrollDistance) &&
        wordTop !== 0
      ) {
        peep.current.scrollTo({
          left: 0,
          top: wordTop,
          behavior: "smooth",
        });
      }
    }
  }, [effectiveTime]);

  // Start playback at sentence start when clicked
  // We're using a delegated event here for performance reasons.
  const handleSentenceClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audio) return;

    const el = e.target as HTMLDivElement;
    // Get our timestamp from the clicked elements data-attribute.
    const at = Number(
      el.getAttribute("data-time-start") ||
        el.parentElement?.getAttribute("data-time-start") ||
        0
    );
    if (at !== undefined) {
      playing ? audio.seek(at) : audio.play(at);
    }
  };

  return (
    <div className={styles.peep} ref={peep} onWheel={suspendScroll}>
      <Typography
        component="span"
        variant="monospace"
        className={cx(styles.root, "highlight-match", className)}
        onClick={handleSentenceClick}
        {...rest}
      >
        <div
          ref={sentencesRef}
          className={styles.sentence}
          data-testid="mention-content"
        >
          …
          {sentences.map(
            ({ sentence, isMention, isRemoved, timings: [start, end] }, i) => {
              const distanceToPrevious =
                i === 0 ? 0 : start - sentences[i - 1].timings[0];
              const isPlaying =
                effectiveTime !== undefined &&
                effectiveTime >= start &&
                effectiveTime < end;

              const before = sentences[i - 1]?.isMention || false;
              const after = sentences[i + 1]?.isMention || false;

              const isFirstPartOfMention = isMention && before === false;
              const isLastPartOfMention = isMention && after === false;

              return (
                <Fragment key={i}>
                  {distanceToPrevious > MAXIMUM_CLUSTER_DISTANCE_SECONDS && (
                    <Typography variant="caption" className={styles.distance}>
                      <div>
                        {durationFromSeconds(distanceToPrevious)}{" "}
                        {L.MENTIONS_KARAOKE_CLIP}
                      </div>
                    </Typography>
                  )}
                  {isFirstPartOfMention && (
                    <>
                      {before !== undefined && i !== 0 && (
                        <em className={styles.brTop} />
                      )}
                    </>
                  )}

                  <span
                    data-time-start={audio ? start : undefined}
                    ref={isPlaying ? active : null}
                    className={cx({
                      [styles.highlight]: playing && isPlaying,
                      [styles.removed]: isRemoved,
                    })}
                    dangerouslySetInnerHTML={{ __html: sentence }}
                  />
                  {i !== sentences.length - 1 && !isLastPartOfMention
                    ? " "
                    : ""}
                  {isLastPartOfMention && (
                    <>
                      {after !== undefined && i !== sentences.length - 1 && (
                        <em className={styles.brBottom} />
                      )}
                    </>
                  )}
                </Fragment>
              );
            }
          )}
          …
        </div>
      </Typography>
    </div>
  );
};

export default Karaoke;
